import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAgents, useCreateAgent } from "@/features/agents/hooks/use-agents";
import { type Agent } from "@/features/agents/types";
import { selectRandomAvatar } from "@/features/agents/utils/avatar-selection";
import { useTranslation } from "@/features/utils/i18n";
import { useState } from "react";
import { toast } from "sonner";
import { AgentForm } from "./agent-form";

interface CreateAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateAgentDialog({
  open,
  onOpenChange,
}: CreateAgentDialogProps) {
  const { t } = useTranslation("agents");
  const createAgent = useCreateAgent();
  const { data: existingAgents = [] } = useAgents();

  const [formData, setFormData] = useState<Partial<Agent>>({
    avatar_id: selectRandomAvatar(undefined, existingAgents),
    name: "",
    role: "",
    identity: "",
    voice: "",
    background_audio: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { agent } = await createAgent.mutateAsync(formData);
      if (!agent?.id) throw new Error("Failed to create agent");
      toast.success(t("agents.messages.created"));
      onOpenChange(false);
      setFormData({
        avatar_id: selectRandomAvatar(undefined, existingAgents),
        name: "",
        role: "",
        identity: "",
        voice: "",
        background_audio: "",
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("agents.messages.createError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("agents.create.title")}</DialogTitle>
          <DialogDescription>
            {t("agents.create.description")}
          </DialogDescription>
        </DialogHeader>

        <AgentForm data={formData} onChange={setFormData} />

        <Button
          type="submit"
          disabled={createAgent.isPending}
          onClick={handleSubmit}
        >
          {createAgent.isPending
            ? t("agents.form.saving")
            : t("agents.create.submitLabel")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
